import { useEffect, useState, useCallback, useRef } from 'react';
import { GameCanvas } from './components/GameCanvas';
import { Header } from './components/Header';
import { HelpModal } from './components/HelpModal';
import { LevelSelectModal } from './components/LevelSelectModal';
import { GameEngine } from './game/GameEngine';
import { Rule } from './types';

function App() {
  const [gameEngine, setGameEngine] = useState<GameEngine | null>(null);
  const [currentDimension, setCurrentDimension] = useState(1);
  const [currentLevel, setCurrentLevel] = useState(1);
  const [rules, setRules] = useState<Rule[]>([]);
  const [completedLevels, setCompletedLevels] = useState<number[]>([]);
  const [helpVisible, setHelpVisible] = useState(false);
  const [levelSelectVisible, setLevelSelectVisible] = useState(false);

  const engineRef = useRef<GameEngine | null>(null);

  const updateFromEngine = useCallback(() => {
    if (!engineRef.current) return;
    setCurrentDimension(engineRef.current.currentDimension);
    setCurrentLevel(engineRef.current.currentLevel);
    setRules([...engineRef.current.rules]);
    setCompletedLevels([...engineRef.current.completedLevels]);
  }, []);

  const handleEngineReady = useCallback((engine: GameEngine) => {
    engineRef.current = engine;
    setGameEngine(engine);
    engine.onUpdate = updateFromEngine;
    engine.loadLevel(1);
    updateFromEngine();
  }, [updateFromEngine]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!gameEngine) return;

      if (e.key === ' ' && gameEngine.gameWon && gameEngine.currentLevel < 10) {
        gameEngine.loadLevel(gameEngine.currentLevel + 1);
        updateFromEngine();
        return;
      }

      if (e.key === 'h' || e.key === 'H') {
        setHelpVisible(prev => !prev);
        return;
      }

      if (e.key === 'r' || e.key === 'R') {
        gameEngine.loadLevel(gameEngine.currentLevel);
        updateFromEngine();
        return;
      }

      if (e.key >= '1' && e.key <= '4') {
        gameEngine.switchDimension(parseInt(e.key));
        updateFromEngine();
        return;
      }

      if (gameEngine.isAnimating) return;

      switch (e.key) {
        case 'ArrowUp':
          gameEngine.handleMove(0, -1);
          e.preventDefault();
          break;
        case 'ArrowDown':
          gameEngine.handleMove(0, 1);
          e.preventDefault();
          break;
        case 'ArrowLeft':
          gameEngine.handleMove(-1, 0);
          e.preventDefault();
          break;
        case 'ArrowRight':
          gameEngine.handleMove(1, 0);
          e.preventDefault();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameEngine, updateFromEngine]);

  const handleSelectLevel = useCallback((level: number) => {
    if (gameEngine) {
      gameEngine.loadLevel(level);
      updateFromEngine();
    }
  }, [gameEngine, updateFromEngine]);

  return (
    <div className="game-wrapper">
      <Header
        currentDimension={currentDimension}
        currentLevel={currentLevel}
        rulesCount={rules.length}
        rules={rules}
        onHelpClick={() => setHelpVisible(true)}
        onLevelSelectClick={() => setLevelSelectVisible(true)}
      />
      
      <GameCanvas
        currentDimension={currentDimension}
        onEngineReady={handleEngineReady}
      />

      <HelpModal
        visible={helpVisible}
        onClose={() => setHelpVisible(false)}
      />

      <LevelSelectModal
        visible={levelSelectVisible}
        completedLevels={completedLevels}
        onClose={() => setLevelSelectVisible(false)}
        onSelectLevel={handleSelectLevel}
      />
    </div>
  );
}

export default App;
